import { Action, Reducer } from "redux";
import { AppThunkAction } from "./";
import { get, post } from "../fetch-interceptor";
import { catchApiError, defaultCatch } from "./utils";
import {
    KnownAction as NotificationsKnownAction,
    actionCreators as NotificationsActionCreators,
    NotificationType
} from "./NotificationsStore";
import { toDate } from "../components/utils/common";
import { Dictionary, UpdateOptions } from "../entities/common";
import { IDeletionResult } from "./services/storeHelper";

export interface TimeReportingPeriodAttrs extends Dictionary<any> {
    Name: string;
    StartDate: Date;
    EndDate: Date;
    Status: TimeReportingPeriodStatus;
    ApprovalRequired: boolean;
}

export interface TimeReportingPeriod {
    id: string;
    attributes: TimeReportingPeriodAttrs;
}

export enum TimeReportingPeriodStatus {
    Open = 0,
    Closed = 1
}

export interface PeriodInfo {
    id: string;
    startDate: Date;
    endDate: Date;
}

export interface BulkCreatePeriodsRequest {
    firstPeriodStartDate: string;
    periodLengthDays: number;
    numberOfPeriods: number;
    prefix?: string;
    nextSequenceNumber: number;
    suffix?: string;
    approvalRequired?: boolean;
}

export interface TimeReportingPeriodUpdate {
    status?: TimeReportingPeriodStatus;
    approvalRequired?: boolean;
}

export interface BulkUpdatePeriodsModel {
    status?: TimeReportingPeriodStatus;
    approvalRequired?: boolean;
}

export interface TimeReportingPeriodsState {
    periods: TimeReportingPeriod[];
    isLoading: boolean;
    isCreating: boolean;
    error: string | null;
    count: number;
    deletionResult?: IDeletionResult[];
    createError?: string;
}

enum ActionTypes {
    LOAD_PERIODS_REQUEST = "TIME_REPORTING_PERIODS/LOAD_REQUEST",
    LOAD_PERIODS_SUCCESS = "TIME_REPORTING_PERIODS/LOAD_SUCCESS",
    LOAD_PERIODS_FAILURE = "TIME_REPORTING_PERIODS/LOAD_FAILURE",
    LOAD_PERIODS_COUNT_SUCCESS = "TIME_REPORTING_PERIODS/LOAD_COUNT_SUCCESS",
    BULK_CREATE_REQUEST = "TIME_REPORTING_PERIODS/BULK_CREATE_REQUEST",
    BULK_CREATE_SUCCESS = "TIME_REPORTING_PERIODS/BULK_CREATE_SUCCESS",
    BULK_CREATE_FAILURE = "TIME_REPORTING_PERIODS/BULK_CREATE_FAILURE",
    UPDATE_REQUEST = "TIME_REPORTING_PERIODS/UPDATE_REQUEST",
    UPDATE_PERIODS_SUCCESS = "TIME_REPORTING_PERIODS/UPDATE_SUCCESS",
    DELETE_REQUEST = "TIME_REPORTING_PERIODS/DELETE_REQUEST",
    RECEIVED_DELETE_PERIODS_RESULT = "TIME_REPORTING_PERIODS/RECEIVED_DELETE_RESULT"
}

interface LoadPeriodsRequest {
    type: ActionTypes.LOAD_PERIODS_REQUEST;
}

interface LoadPeriodsSuccess {
    type: ActionTypes.LOAD_PERIODS_SUCCESS;
    periods: TimeReportingPeriod[];
}

interface LoadPeriodsFailure {
    type: ActionTypes.LOAD_PERIODS_FAILURE;
    error: string;
}

interface LoadPeriodsCountSuccess {
    type: ActionTypes.LOAD_PERIODS_COUNT_SUCCESS;
    count: number;
}

interface BulkCreateRequest {
    type: ActionTypes.BULK_CREATE_REQUEST;
}

interface BulkCreateSuccess {
    type: ActionTypes.BULK_CREATE_SUCCESS;
    periods: TimeReportingPeriod[];
}

interface BulkCreateFailure {
    type: ActionTypes.BULK_CREATE_FAILURE;
    error?: string;
}

interface UpdateRequest {
    type: ActionTypes.UPDATE_REQUEST;
}

interface UpdatePeriodsSuccess {
    type: ActionTypes.UPDATE_PERIODS_SUCCESS;
    periods: TimeReportingPeriod[];
}

interface DeleteRequest {
    type: ActionTypes.DELETE_REQUEST;
}

interface ReceivedDeletePeriodsResultAction {
    type: ActionTypes.RECEIVED_DELETE_PERIODS_RESULT;
    deletionResult?: IDeletionResult[];
}

type KnownAction =
    | LoadPeriodsRequest
    | LoadPeriodsSuccess
    | LoadPeriodsFailure
    | LoadPeriodsCountSuccess
    | BulkCreateRequest
    | BulkCreateSuccess
    | BulkCreateFailure
    | UpdateRequest
    | UpdatePeriodsSuccess
    | DeleteRequest
    | ReceivedDeletePeriodsResultAction;

const emptyUpdateResult: UpdatePeriodsSuccess = {
    type: ActionTypes.UPDATE_PERIODS_SUCCESS,
    periods: []
};

export const actionCreators = {
    loadPeriods: (): AppThunkAction<KnownAction> => dispatch => {
        dispatch({ type: ActionTypes.LOAD_PERIODS_REQUEST });
        get<TimeReportingPeriod[]>("api/timereportingperiods")
            .then(periods => dispatch({ type: ActionTypes.LOAD_PERIODS_SUCCESS, periods }))
            .catch(defaultCatch(dispatch));
    },

    loadPeriodsCount: (): AppThunkAction<KnownAction> => dispatch => {
        get<number>("api/timereportingperiods/count")
            .then(count => dispatch({ type: ActionTypes.LOAD_PERIODS_COUNT_SUCCESS, count }))
            .catch(defaultCatch(dispatch));
    },

    bulkCreatePeriods:
        (request: BulkCreatePeriodsRequest, callback?: () => void): AppThunkAction<KnownAction | NotificationsKnownAction> =>
        dispatch => {
            dispatch({ type: ActionTypes.BULK_CREATE_REQUEST });
            post<TimeReportingPeriod[]>("api/timereportingperiods/bulk/create", request)
                .then(periods => {
                    dispatch({ type: ActionTypes.BULK_CREATE_SUCCESS, periods });
                    callback?.();
                })
                .catch(
                    catchApiError(_ => {
                        dispatch({ type: ActionTypes.BULK_CREATE_FAILURE, error: _ });
                    })
                );
        },

    bulkUpdatePeriods:
        (ids: string[], update: BulkUpdatePeriodsModel, options?: UpdateOptions): AppThunkAction<KnownAction | NotificationsKnownAction, Promise<void>> =>
        dispatch => {
            if (!options?.inlineEdit) {
                dispatch({ type: ActionTypes.UPDATE_REQUEST });
            }
            return post<TimeReportingPeriod[]>("api/timereportingperiods/bulk/update", { ids, ...update })
                .then(periods => {
                    dispatch({ type: ActionTypes.UPDATE_PERIODS_SUCCESS, periods });
                })
                .catch(err => {
                    catchApiError(_ => {
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }));
                        if (!options?.inlineEdit) {
                            dispatch(emptyUpdateResult);
                        }
                    })(err);
                    throw err;
                });
        },

    deletePeriods:
        (ids: string[], callback?: () => void): AppThunkAction<KnownAction | NotificationsKnownAction> =>
        dispatch => {
            dispatch({ type: ActionTypes.DELETE_REQUEST });
            post<IDeletionResult[]>("api/timereportingperiods/bulk/delete", { ids })
                .then(data => {
                    dispatch({ type: ActionTypes.RECEIVED_DELETE_PERIODS_RESULT, deletionResult: data });
                    callback?.();
                })
                .catch(
                    catchApiError(_ => {
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }));
                        dispatch({ type: ActionTypes.RECEIVED_DELETE_PERIODS_RESULT });
                    })
                );
        },

    dismissDeletionResult: (): AppThunkAction<KnownAction> => dispatch => {
        dispatch({ type: ActionTypes.RECEIVED_DELETE_PERIODS_RESULT });
    },

    dismissCreateError: (): AppThunkAction<KnownAction> => dispatch => {
        dispatch({ type: ActionTypes.BULK_CREATE_FAILURE });
    }
};

const initialState: TimeReportingPeriodsState = {
    periods: [],
    isLoading: true,
    isCreating: false,
    error: null,
    count: 0
};

export const reducer: Reducer<TimeReportingPeriodsState> = (
    state: TimeReportingPeriodsState = initialState,
    incomingAction: Action
) => {
    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionTypes.LOAD_PERIODS_REQUEST:
        case ActionTypes.UPDATE_REQUEST:
        case ActionTypes.DELETE_REQUEST:
            return { ...state, isLoading: true, error: null, deletionResult: undefined, createError: undefined };

        case ActionTypes.LOAD_PERIODS_SUCCESS:
            return { ...state, isLoading: false, periods: _checkDates(action.periods) };

        case ActionTypes.LOAD_PERIODS_FAILURE:
            return { ...state, isLoading: false, error: action.error };

        case ActionTypes.LOAD_PERIODS_COUNT_SUCCESS:
            return { ...state, count: action.count };

        case ActionTypes.BULK_CREATE_REQUEST:
            return { ...state, isCreating: true, createError: undefined };

        case ActionTypes.BULK_CREATE_SUCCESS:
            return { ...state, isCreating: false, periods: [...state.periods, ..._checkDates(action.periods)] };

        case ActionTypes.BULK_CREATE_FAILURE:
            return { ...state, isCreating: false, isLoading: false, createError: action.error };

        case ActionTypes.UPDATE_PERIODS_SUCCESS: {
            const updated = _checkDates(action.periods);

            return {
                ...state,
                isLoading: false,
                periods: state.periods.map(p => updated.find(_ => _.id === p.id) ?? p)
            };
        }

        case ActionTypes.RECEIVED_DELETE_PERIODS_RESULT: {
            const periods = action.deletionResult?.some(_ => _.isDeleted)
                ? state.periods.filter(p => !action.deletionResult!.some(_ => _.id === p.id && _.isDeleted))
                : state.periods;

            return {
                ...state,
                isLoading: false,
                periods,
                deletionResult: action.deletionResult
            };
        }

        default:
            return state;
    }
};

const _checkDates = (periods: TimeReportingPeriod[]) => {
    periods.forEach(p => {
        p.attributes.StartDate = toDate(p.attributes.StartDate)!.getBeginOfDay();
        p.attributes.EndDate = toDate(p.attributes.EndDate)!.getEndOfDay();
    });

    return periods;
};

export const findPeriodForDate = (date: Date, periods: TimeReportingPeriod[]): TimeReportingPeriod | undefined => {
    return periods.find(p => date >= p.attributes.StartDate && date <= p.attributes.EndDate);
};

export const isDateInClosedPeriod = (date: Date, periods: TimeReportingPeriod[]): boolean => {
    const period = findPeriodForDate(date, periods);
    if (!period) {
        return true;
    }
    return period.attributes.Status === TimeReportingPeriodStatus.Closed;
};

export const isPeriodOpenForReporting = (period: TimeReportingPeriod | null | undefined): boolean => {
    return !!period && period.attributes.Status === TimeReportingPeriodStatus.Open;
};

export const getEarliestOpenPeriodDate = (periods: TimeReportingPeriod[]): Date | undefined => {
    const openPeriods = periods.filter(p => p.attributes.Status === TimeReportingPeriodStatus.Open);
    return openPeriods[0]?.attributes.StartDate;
};

export const getLatestOpenPeriodDate = (periods: TimeReportingPeriod[]): Date | undefined => {
    const openPeriods = periods.filter(p => p.attributes.Status === TimeReportingPeriodStatus.Open);
    return openPeriods[openPeriods.length - 1]?.attributes.EndDate;
};
